import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getUrlParams, getGoqiiUserId } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./PSQICalculator.css";

const FREQ_OPTIONS = [
  "psqi.freq.notDuringPastMonth",
  "psqi.freq.lessThanOnceAWeek",
  "psqi.freq.onceOrTwiceAWeek",
  "psqi.freq.threeOrMoreTimesAWeek",
];

// Fixed English values the backend expects in logQuestions, independent of the
// display language — these must not be swapped for translated text.
const FREQ_VALUES = [
  "Not during past month",
  "Less than once a week",
  "Once or twice a week",
  "Three or more times a week",
];

const SLEEP_QUALITY = [
  "psqi.sleepQuality.veryGood",
  "psqi.sleepQuality.fairlyGood",
  "psqi.sleepQuality.fairlyBad",
  "psqi.sleepQuality.veryBad",
];

const SLEEP_QUALITY_VALUES = ["Very good", "Fairly good", "Fairly bad", "Very bad"];

const ENTHUSIASM = [
  "psqi.enthusiasm.noProblem",
  "psqi.enthusiasm.slightProblem",
  "psqi.enthusiasm.somewhatProblem",
  "psqi.enthusiasm.veryBigProblem",
];

const ENTHUSIASM_VALUES = [
  "No problem at all",
  "Only a very slight problem",
  "Somewhat of a problem",
  "A very big problem",
];

const Q5to11 = [
  { qKey: "psqi.q5", id: "5" },
  { qKey: "psqi.q6", id: "6" },
  { qKey: "psqi.q7", id: "7" },
  { qKey: "psqi.q8", id: "8" },
  { qKey: "psqi.q9", id: "9" },
  { qKey: "psqi.q10", id: "10" },
  { qKey: "psqi.q11", id: "11" },
];

const Q16to20 = [
  { qKey: "psqi.q16", id: "16" },
  { qKey: "psqi.q17", id: "17" },
  { qKey: "psqi.q18", id: "18" },
  { qKey: "psqi.q19", id: "19" },
  { qKey: "psqi.q20", id: "20" },
];

const PSQICalculator = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const urlParams = new URLSearchParams(window.location.search);
  const uuid = urlParams.get("uuid") || "";
  const goqiiUserId = getGoqiiUserId();
  const nonce = urlParams.get("nonce") || "";
  const apiKey = urlParams.get("apiKey") || "";
  const signature = urlParams.get("signature") || "";
  const goqiiAccessToken = urlParams.get("goqiiAccessToken") || "";

  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [theme, lang]);

  const set = (id, value) => setAnswers((prev) => ({ ...prev, [id]: value }));
  const isComplete = Object.keys(answers).length === 20;

  const buildQs = () =>
    `uuid=${uuid}&goqiiuserid=${goqiiUserId}&nonce=${nonce}&apiKey=${apiKey}&signature=${signature}&goqiiAccessToken=${goqiiAccessToken}&theme=${theme}&lang=${lang}`;

  const buildLogQuestions = () => {
    const out = {};
    Object.keys(answers).forEach((id) => {
      out[`question${id}`] = answers[id];
    });
    return out;
  };

  const handleSubmit = () => {
    if (!isComplete || submitting) return;
    setSubmitting(true);
    lifestyleApi
      .logPsqi({ goqiiUserId, logQuestions: JSON.stringify(buildLogQuestions()) })
      .then((res) => {
        if (String(res?.data?.code) === "200") {
          window.location.href = `/psqidermadetails?${buildQs()}&type=psqi`;
        }
      })
      .catch((err) => console.error("logPsqi:", err))
      .finally(() => setSubmitting(false));
  };

  const tableRadio = (id, i, checked) => (
    <label className="psqi-radio-wrap">
      <input
        type="radio"
        name={`q${id}`}
        checked={checked}
        onChange={() => set(id, FREQ_VALUES[i])}
      />
      <span className={`psqi-radio${checked ? " checked" : ""}`} />
    </label>
  );

  return (
    <>
      <section className="psqi-page">
        <div className="psqi-container">

          {/* Q1 – Bedtime */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.q1")}</label>
            <input
              type="time"
              className="psqi-input"
              value={answers["1"] || ""}
              onChange={(e) => set("1", e.target.value)}
            />
          </div>

          {/* Q2 – Minutes to fall asleep */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.q2")}</label>
            <input
              type="number"
              min={0}
              className="psqi-input"
              placeholder="0"
              value={answers["2"] || ""}
              onChange={(e) => set("2", e.target.value)}
            />
          </div>

          {/* Q3 – Wake time */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.q3")}</label>
            <input
              type="time"
              className="psqi-input"
              value={answers["3"] || ""}
              onChange={(e) => set("3", e.target.value)}
            />
          </div>

          {/* Q4 – Hours of sleep */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.q4")}</label>
            <input
              type="number"
              min={0}
              max={24}
              className="psqi-input"
              placeholder="0"
              value={answers["4"] || ""}
              onChange={(e) => set("4", e.target.value)}
            />
          </div>

          {/* Q5–11 frequency table */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.tableTitle1")}</label>
            <div className="psqi-table-wrap">
              <table className="psqi-table">
                <thead>
                  <tr>
                    <th className="left-col">{t("psqi.colQuestion")}</th>
                    {FREQ_OPTIONS.map((k, i) => (
                      <th key={i}>{t(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Q5to11.map(({ qKey, id }) => (
                    <tr key={id}>
                      <td className="left-col">{t(qKey)}</td>
                      {FREQ_OPTIONS.map((_, i) => (
                        <td key={i}>{tableRadio(id, i, answers[id] === FREQ_VALUES[i])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Q12 – Medicine frequency */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.q12")}</label>
            <div className="psqi-radio-group">
              {FREQ_OPTIONS.map((k, i) => (
                <label key={i} className="psqi-radio-item">
                  <input
                    type="radio"
                    name="q12"
                    checked={answers["12"] === FREQ_VALUES[i]}
                    onChange={() => set("12", FREQ_VALUES[i])}
                  />
                  <span className={`psqi-radio${answers["12"] === FREQ_VALUES[i] ? " checked" : ""}`} />
                  <span className="psqi-radio-label">{t(k)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Q13 – Trouble staying awake */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.q13")}</label>
            <div className="psqi-radio-group">
              {FREQ_OPTIONS.map((k, i) => (
                <label key={i} className="psqi-radio-item">
                  <input
                    type="radio"
                    name="q13"
                    checked={answers["13"] === FREQ_VALUES[i]}
                    onChange={() => set("13", FREQ_VALUES[i])}
                  />
                  <span className={`psqi-radio${answers["13"] === FREQ_VALUES[i] ? " checked" : ""}`} />
                  <span className="psqi-radio-label">{t(k)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Q14 – Enthusiasm */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.q14")}</label>
            <div className="psqi-radio-group">
              {ENTHUSIASM.map((k, i) => (
                <label key={i} className="psqi-radio-item">
                  <input
                    type="radio"
                    name="q14"
                    checked={answers["14"] === ENTHUSIASM_VALUES[i]}
                    onChange={() => set("14", ENTHUSIASM_VALUES[i])}
                  />
                  <span className={`psqi-radio${answers["14"] === ENTHUSIASM_VALUES[i] ? " checked" : ""}`} />
                  <span className="psqi-radio-label">{t(k)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Q15 – Overall sleep quality */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.q15")}</label>
            <div className="psqi-radio-group">
              {SLEEP_QUALITY.map((k, i) => (
                <label key={i} className="psqi-radio-item">
                  <input
                    type="radio"
                    name="q15"
                    checked={answers["15"] === SLEEP_QUALITY_VALUES[i]}
                    onChange={() => set("15", SLEEP_QUALITY_VALUES[i])}
                  />
                  <span className={`psqi-radio${answers["15"] === SLEEP_QUALITY_VALUES[i] ? " checked" : ""}`} />
                  <span className="psqi-radio-label">{t(k)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Q16–20 frequency table */}
          <div className="psqi-question-block">
            <label className="psqi-label">{t("psqi.tableTitle2")}</label>
            <div className="psqi-table-wrap">
              <table className="psqi-table">
                <thead>
                  <tr>
                    <th className="left-col">{t("psqi.colQuestion")}</th>
                    {FREQ_OPTIONS.map((k, i) => (
                      <th key={i}>{t(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Q16to20.map(({ qKey, id }) => (
                    <tr key={id}>
                      <td className="left-col">{t(qKey)}</td>
                      {FREQ_OPTIONS.map((_, i) => (
                        <td key={i}>{tableRadio(id, i, answers[id] === FREQ_VALUES[i])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </section>

      <section className="psqi-bottom-btn">
        <button
          type="button"
          className="psqi-btn-done"
          disabled={!isComplete || submitting}
          onClick={handleSubmit}
        >
          {t("psqi.submitBtn")}
        </button>
      </section>
    </>
  );
};

export default PSQICalculator;
